import { useEffect, useState } from 'react';
import { useSetupStore } from '../../store';
import { useStore } from '@/store';
import { useLoadState } from '@/hooks';
import { useTemplate, useTemplateDeploy, useUpdateTemplate } from '@/api';
import { useNavigate } from 'react-router';

export const useManageTemplate = (
  id: string,
  enabled: boolean,
  isStatic: boolean,
) => {
  /* Global State */
  const [setId, setIsDeployOnly, isDeployOnly, setTextContent] = useStore(
    ({ config: c }) => [
      c.setId,
      c.setIsDeployOnly,
      c.getIsDeployOnly(),
      c.setTextContent,
    ],
  );
  /* Context State */
  const [currentStep, setCurrentStep, setTemplateId] = useSetupStore((s) => [
    s.currentStep,
    s.setCurrentStep,
    s.setSelectedTemplateId,
  ]);
  /* Local State */
  const [active, setActive] = useState(false);
  /* Hooks */
  const { load } = useLoadState();
  const navigate = useNavigate();
  /* Queries */
  const { mutate: updateTemplate } = useUpdateTemplate();
  const { data: template } = useTemplate(id);
  const { data: deploy } = useTemplateDeploy(id, {
    // We already have access to the deploy path so we can skip templates that don't have it.
    enabled: !!template?.deployPath,
  });

  /** Effects */
  useEffect(() => {
    if (!deploy) return;
    if (!isDeployOnly) return;

    setId(id);
    setTextContent(deploy);
    navigate('/editor');
  }, [deploy, isDeployOnly]);

  useEffect(() => {
    if (!template) return;
    if (!active) return;
    if (!template.save) return;

    setId(id);
    load(template.save);
    setCurrentStep(currentStep + 1);
  }, [template, active]);

  /** Handlers */
  const handleEnable = () => {
    updateTemplate({
      id,
      template: {
        enabled: !enabled,
      },
    });
  };

  const handleSelect = () => {
    if (template && !template.save) {
      setIsDeployOnly(true);
    }
    setActive(true);
    setTemplateId(id);
  };

  return {
    handleEnable,
    handleSelect,
  };
};
